<template>
  <div>
    <MissedCallsBanner
      v-if="showMarketContext"
      :total-missed-calls="totalMissedCalls"
      :campaigns-below-market="campaignsBelowMarketCount"
      :window-label="marketWindow.long"
      class="m-4 mb-0"
      @review="sortByMissedCalls"
    />

    <Panel
      :icon="['fas', 'chart-line']"
      loader-text="Loading Campaigns"
      :loading="loading"
      :show-loading-error="showLoadingError"
      :loading-error-text="loadingErrorText"
    >
      <template #header>
        <span data-cy="heading">Campaigns</span>
      </template>
      <template #content>
        <!--
          Staff-only note. The two things impersonation deliberately does NOT carry are creating a
          softphone campaign (its destination is composed from the acting user, so an admin's would
          ring an agent id with no credential) and going live (that registers a SIP identity, which
          must stay the agent's own). Editing is fine. Says so up front rather than leaving an
          admin hunting for a Create item and a Go Live button that are not coming.
        -->
        <b-alert
          v-if="showSoftphoneAdminNotice"
          show
          variant="info"
          class="p-2"
          data-cy="softphone-admin-notice"
        >
          You are viewing this account as an admin. You can edit its softphone campaigns, but only
          the account's own softphone agents can create one or take it live.
        </b-alert>

        <!-- The market endpoint stopped at its row cap. The campaigns past it would otherwise
             render a dash exactly like a market with no auctions, and drop out of the banner's
             missed-calls total, with nothing on screen to say either is incomplete -->
        <b-alert
          v-if="showMarketContext && campaignMarketPositionsTruncated"
          show
          variant="warning"
          class="p-2"
          data-cy="market-positions-truncated"
        >
          {{ truncatedMarketPositionsText }}
        </b-alert>

        <b-table
          data-cy="table-buyer-campaigns"
          :tbody-tr-attr="{ 'data-cy': 'table-row' }"
          :items="campaignsWithMarketData"
          :fields="visibleFields"
          primary-key="id"
          responsive
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          @update:sortBy="sortBy = $event"
          @update:sortDesc="sortDesc = $event"
        >
          <!--
            A softphone campaign's row says what the WIDGET says - see `statusText`. The agent reads
            this table and the widget together, and one of them calling the same moment "Active" and
            the other "Ready" leaves them working out which is which.
          -->
          <template #cell(status)="row">
            <b-badge
              class="campaign-status"
              :variant="statusVariant(row.item)"
              data-cy="campaign-status"
            >
              {{ statusText(row.item) }}
            </b-badge>
          </template>

          <template #cell(category.name)="row">
            {{ row.item.category.name }}
            <b-badge
              v-if="row.item.category.isCpaConversionOnly"
              variant="info"
              class="ml-1"
              title="CPA Conversion Only Category"
            >
              CPA
            </b-badge>
          </template>

          <template #cell(maxBidPerCallInUSD)="row">
            <!-- Static campaigns get the same inline bid input the dashboard uses, inside the
                 rx-market-context rollout (see canEditBidInline); RTB and Programmatic bids are
                 set by the bidder, so they stay read-only -->
            <!-- Keyed on the campaign alone: the input follows a changed bid itself, so a save
                 or a raise never remounts it under the buyer's cursor, which would also flush
                 whatever they were in the middle of typing -->
            <CampaignBidInput
              v-if="canEditBidInline(row.item)"
              :key="row.item.id"
              class="inline-bid-input"
              :campaign-id="row.item.id"
              :initial-value="Number(row.item.budget.maxBidPerCallInUSD)"
              :min-bid="getCategoryMinBid(row.item)"
              @saved="onInlineBidSaved"
            />
            <template v-else>
              {{ getMaxBidPerCall(row.item) }}
            </template>
          </template>

          <template #head(marketPosition)="data">
            {{ data.label }}
            <span
              v-b-tooltip.hover.left
              :title="marketPositionTooltip"
              data-cy="tooltip-market-position"
            >
              <font-awesome-icon
                class="ml-1"
                :icon="['fas', 'question-circle']"
              />
            </span>
          </template>

          <template #cell(marketPosition)="row">
            <!-- The bid marker comes from the endpoint's positionedBidInUSD, never from the
                 campaign config, so the marker always sits on the exact bid the tier was
                 computed from - configured max bid for Static, the median observed over the
                 market window for RTB/Programmatic - and the two can never drift -->
            <MarketPositionIndicator
              :market-position="row.item.marketPosition"
              :bid-in-usd="getPositionedBid(row.item)"
              :window-short="marketWindow.short"
              :is-static="isStaticCampaign(row.item)"
            />
          </template>

          <template #head(missedCalls)="data">
            {{ data.label }}
            <span
              v-b-tooltip.hover.left
              :title="missedCallsTooltip"
              data-cy="tooltip-missed-calls"
            >
              <font-awesome-icon
                class="ml-1"
                :icon="['fas', 'question-circle']"
              />
            </span>
          </template>

          <template #cell(missedCalls)="row">
            <span
              v-if="row.item.missedCalls !== null"
              :class="getMissedCallsClass(row.item)"
            >
              {{ row.item.missedCalls.toLocaleString() }}
            </span>
            <span
              v-else
              class="text-muted"
            >-</span>
          </template>

          <template #head(opportunity)="data">
            {{ data.label }}
            <span
              v-b-tooltip.hover.left
              :title="opportunityTooltip"
              data-cy="tooltip-opportunity"
            >
              <font-awesome-icon
                class="ml-1"
                :icon="['fas', 'question-circle']"
              />
            </span>
          </template>

          <template #cell(opportunity)="row">
            <BidOpportunityCell
              :campaign="row.item"
              :market-position="row.item.marketPosition"
              :is-static="isStaticCampaign(row.item)"
              :window-short="marketWindow.short"
              @apply="showConfirmRaiseBid"
            />
          </template>

          <template #cell(dailyBudgetInUSD)="row">
            {{ formatCurrency(row.item.budget.dailyBudgetInUSD) }}
          </template>

          <template #cell(actions)="row">
            <b-button
              v-if="canPauseCampaign(row.item) && !isCampaignPaused(row.item)"
              v-b-tooltip.hover.left
              class="bg-transparent border-0 p-0"
              size="sm"
              title="Pause Campaign"
              @click="showConfirmPauseBuyerCampaign(row.item)"
            >
              <font-awesome-icon
                :icon="['fa', 'pause']"
              />
            </b-button>
            <b-button
              v-if="canPauseCampaign(row.item) && isCampaignPaused(row.item)"
              v-b-tooltip.hover.left
              class="bg-transparent border-0 p-0"
              size="sm"
              title="Resume Campaign"
              @click="showConfirmResumeBuyerCampaign(row.item)"
            >
              <font-awesome-icon
                :icon="['fa', 'play']"
              />
            </b-button>
            <b-button
              v-if="showSoftphoneButton(row.item)"
              v-b-tooltip.hover.left
              class="ml-3"
              size="sm"
              :variant="isLiveSoftphoneCampaign(row.item) ? 'danger' : 'success'"
              :disabled="isSoftphoneButtonDisabled(row.item)"
              :title="softphoneButtonTitle(row.item)"
              :data-cy="isLiveSoftphoneCampaign(row.item) ? 'button-softphone-go-offline' : 'button-softphone-go-live'"
              @click="toggleSoftphoneForCampaign(row.item)"
            >
              <font-awesome-icon
                :icon="['fa', isLiveSoftphoneCampaign(row.item) ? 'phone-slash' : 'phone']"
                class="mr-1"
              />
              {{ isLiveSoftphoneCampaign(row.item) ? 'Go Offline' : 'Go Live' }}
            </b-button>
            <b-button
              :to="{
                name: editRouteName(row.item),
                params: { id: row.item.id }
              }"
              data-cy="button-edit"
              class="bg-transparent border-0 p-0 ml-3"
              size="sm"
            >
              <font-awesome-icon
                :icon="['fa', 'edit']"
              />
            </b-button>
          </template>
        </b-table>
      </template>
    </Panel>

    <b-modal
      id="confirm-pause-modal"
      ref="confirm-pause-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Pause"
      ok-title="Pause"
      button-size="sm"
      @ok="pauseBuyerCampaign"
    >
      <p
        v-if="selectedBuyerCampaign"
        class="mb-2"
      >
        Are you sure you want to pause <strong>{{ selectedBuyerCampaign.name }}</strong>?
      </p>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="$bvModal.hide('confirm-pause-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          @click="pauseBuyerCampaign"
        >
          Pause
        </BaseButtonLoader>
      </template>
    </b-modal>

    <b-modal
      id="confirm-resume-modal"
      ref="confirm-resume-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Resume"
      ok-title="Resume"
      button-size="sm"
      @ok="resumeBuyerCampaign"
    >
      <p
        v-if="selectedBuyerCampaign"
        class="mb-2"
      >
        Are you sure you want to resume <strong>{{ selectedBuyerCampaign.name }}</strong>?
      </p>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="$bvModal.hide('confirm-resume-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          @click="resumeBuyerCampaign"
        >
          Resume
        </BaseButtonLoader>
      </template>
    </b-modal>

    <b-modal
      id="confirm-raise-bid-modal"
      ref="confirm-raise-bid-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Bid Change"
      button-size="sm"
    >
      <p
        v-if="selectedBidRaise"
        class="mb-2"
      >
        Raise the bid on <strong>{{ selectedBidRaiseCampaign.name }}</strong> from
        <strong>{{ formatCurrency(selectedBidRaiseCampaign.budget.maxBidPerCallInUSD) }}</strong> to
        <strong>{{ formatCurrency(selectedBidRaise.suggestedBidInUSD) }}</strong>?
      </p>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="$bvModal.hide('confirm-raise-bid-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          data-cy="button-confirm-raise-bid"
          @click="applySuggestedBid"
        >
          Raise Bid
        </BaseButtonLoader>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import CampaignBidInput from '@/components/buyer/dashboard/CampaignBidInput.vue'
import MarketPositionIndicator from './MarketPositionIndicator.vue'
import BidOpportunityCell from './BidOpportunityCell.vue'
import MissedCallsBanner from './MissedCallsBanner.vue'

import { mapFeatureFlags } from '@/plugins/featureFlags'
import formatCurrency from '@/utils/formatCurrency'
import { FEATURE_FLAG_KEYS } from '@/utils/featureFlagKeys'
import { showErrorMessage, showSuccessMessage, showWarningMessage } from '@/notification-utils'
import { canSoftphoneCampaignGoLive, getBadgeVariantForCampaignStatus, getCampaignRoutingLabel, getSoftphoneGoLiveBlockedReason, getStatusText, isSoftphoneCampaign, PAUSED_STATUS, campaignRoutingTypes } from '@/store/modules/campaign.store'
import { getMinBidMessage, getStaticCampaignMinBid } from '@/utils/campaignMinBid'
import {
  bidTiers,
  formatMarketWindow,
  getMarketPositionTooltip,
  getOpportunityTooltip,
  toCents
} from '@/store/modules/market-context.store'
import { BLOCKED_STATUSES, getSoftphoneStatusBadgeVariant, getSoftphoneStatusLabel, SOFTPHONE_STATUS } from '@/store/modules/softphone.store'

/**
 * The columns a softphone agent does not get. Every one of them is either fixed for all softphone
 * campaigns - US only, one static agent destination, one call type - or bookkeeping meant for the
 * account rather than the agent: the campaign's name is generated from the category and the
 * agent's own name, so the Category column beside it already says the only part that varies.
 */
const COLUMNS_HIDDEN_FROM_AGENTS = Object.freeze([
  'name',
  'country.name',
  'routing.type',
  'trafficSources.callType'
])

// The market columns, which go only where `showMarketContext` does: never to a softphone agent (see
// `marketContext/canViewMarketContext`), and only inside the rx-market-context rollout. A separate
// list from the one above because the reason is separate: those say the same thing on every
// softphone row, these are market data a softphone agent is not shown at all.
const MARKET_COLUMNS = Object.freeze(['marketPosition', 'missedCalls', 'opportunity'])

// A short grace period after the agent goes offline before Go Live is offered again: the widget
// pauses the campaign as it shuts down, and going live again inside that window races the pause.
const GO_LIVE_COOLDOWN_MS = 1000

// How long the softphone is given to register before the agent is told it has not connected.
// Generous on purpose: it fetches a WebRTC token, opens a websocket and registers - and when the
// softphone is running in another tab, all of that happens over there.
const SOFTPHONE_CONNECT_TIMEOUT_MS = 30000

// A failed go-live resets the widget to offline first and sets the reason a mutation later. This is
// how long the offline is given to be followed by that reason before the agent is told without one.
const SOFTPHONE_FAILURE_REASON_GRACE_MS = 500

// The agent is taking calls in every one of these - READY is the usual one, the rest cover a call
// that arrives before the mirror has seen the widget go ready.
const CONNECTED_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.READY,
  SOFTPHONE_STATUS.RINGING,
  SOFTPHONE_STATUS.ACTIVE,
  SOFTPHONE_STATUS.WRAP_UP,
  /**
   * The blocked statuses are CONNECTED. The registration landed and the go-live worked; the account
   * merely cannot buy what the registration would answer, and the widget says so itself.
   *
   * Left out, they are not a wrong label but a wedge: registration commits `SET_STATUS(READY)`,
   * which resolves to a blocked status when the account is capped, so this wait would match nothing
   * and sit until its 30 second timeout - warning the agent that they are "not connected yet" when
   * they are, and holding `isTogglingSoftphone` for all 30 seconds. That flag is the FIRST branch of
   * `isSoftphoneButtonDisabled`, so it would also kill Go Offline on the row they just went live for.
   *
   * Reachable despite the check in `goLiveForCampaign`: the account only has to be capped in the
   * seconds between that check and the registration landing.
   */
  ...BLOCKED_STATUSES
])

export default {
  name: 'ListBuyerCampaigns',

  title: 'Campaigns',

  components: {
    CampaignBidInput,
    MarketPositionIndicator,
    BidOpportunityCell,
    MissedCallsBanner
  },

  data() {
    return {
      loading: true,
      showLoadingError: false,
      loadingErrorText: '',
      selectedBuyerCampaign: null,
      selectedBidRaise: null,
      isLoading: false,
      isTogglingSoftphone: false,
      isGoLiveCoolingDown: false,
      goLiveCooldownTimer: null,
      sortBy: '',
      sortDesc: false,
      // The campaigns and the SOFTPHONE_GOES_LIVE setting have both loaded. Stays false when the
      // campaigns fail to load, so no market fetch runs under a load error.
      campaignsLoaded: false,
      marketPositionsRefreshTimeout: null
    }
  },

  computed: {
    ...mapState('campaign', [
      'buyerCampaigns'
    ]),

    ...mapGetters('marketContext', [
      'getMarketPositionForCampaign',
      'campaignMarketPositionsWindowHours',
      'campaignMarketPositionsTruncated',
      'campaignMarketPositionsLimit',
      'canViewMarketContext'
    ]),

    ...mapFeatureFlags({ marketContextEnabled: FEATURE_FLAG_KEYS.marketContext.key }),

    // Market columns, banner and the positions behind them: once the campaigns and the softphone
    // setting have loaded (canViewMarketContext fails open until then), for anyone it allows (not a
    // softphone agent), inside the rx-market-context rollout. The flag is read last: its first read
    // logs a Datadog exposure.
    showMarketContext() {
      return this.campaignsLoaded && this.canViewMarketContext && this.marketContextEnabled
    },

    ...mapGetters('globalSettings', ['isSoftphoneUser', 'canViewSoftphoneCampaigns']),

    ...mapGetters('auth', ['userId', 'isImpersonating']),

    ...mapGetters('softphone', [
      'isSoftphoneAvailable',
      'liveCampaignId',
      'softphoneStatusLabel'
    ]),

    ...mapState('softphone', {
      softphoneStatus: state => state.status
    }),

    ...mapState('account', ['accountUsers']),

    // The buyer balance endpoint's answer, refreshed every 45 seconds by the header - see
    // AccountSummaryHeader. What the Go Live button reads to know whether a shift started now
    // could ring at all.
    ...mapState('billing', ['accountBalance']),

    // The window every market number in this table was measured over - 24h on six days of the
    // week, 7d on Mondays - as { short, long } label strings. Both are EMPTY until
    // fetchCampaignMarketPositions resolves, so the labels render bare rather than showing
    // "24h" and then flipping to "7d" a moment later
    marketWindow() {
      return formatMarketWindow(this.campaignMarketPositionsWindowHours)
    },

    // Lives in computed, not data, only because the Missed column's label is data-driven now.
    // Object.freeze stays INSIDE the computed on purpose: it stops BootstrapVue's field
    // normalisation mutating these source objects, which is a correctness guard rather than a
    // micro-optimisation. The formatter arrows below resolve `this` identically here and in
    // data(), since statusText is a method and startCase comes from the global mixin
    fields() {
      return Object.freeze([
        {
          key: 'status',
          sortable: true,
          // b-table sorts on `item.status`, which is the server's word and not the one in the cell
          // - an agent clicking the header would reorder a column of "Offline" badges by a value
          // nobody can see. Sorting on the rendered text is the only sort that looks like one.
          formatter: (value, key, item) => this.statusText(item),
          sortByFormatted: true,
          thClass: 'text-center',
          tdClass: 'text-center'
        },
        {
          key: 'name',
          sortable: true,
        },
        {
          key: 'category.name',
          label: 'Category',
          sortable: true,
        },
        {
          key: 'country.name',
          label: 'Country',
          sortable: true,
        },
        {
          key: 'language.name',
          label: 'Language',
          sortable: true,
        },
        {
          key: 'routing.type',
          label: 'Routing',
          sortable: true,
          formatter: (value, key, item) => {
            return getCampaignRoutingLabel(item, this.startCase)
          }
        },
        {
          key: 'trafficSources.callType',
          label: 'Call Type',
          sortable: true
        },
        {
          key: 'maxBidPerCallInUSD',
          label: 'Bid',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'marketPosition',
          label: 'Market Position',
        },
        {
          key: 'missedCalls',
          label: this.marketWindow.short ? `Missed (${this.marketWindow.short})` : 'Missed',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'opportunity',
          label: 'Opportunity',
        },
        {
          key: 'dailyBudgetInUSD',
          label: 'Daily Budget',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right',
        },
        {
          key: 'actions',
          tdAttr: {
            'data-cy': 'table-column-actions'
          }
        }
      ])
    },

    // Whether anyone on this account actually takes softphone calls. Read off the account's user
    // list (`softphoneAgent` per row - the same field admin-ui toggles) rather than off the
    // campaigns, so the note still appears for an account whose agents have not created a campaign
    // yet, which is exactly when "you cannot create one for them" is worth saying.
    accountHasSoftphoneAgents() {
      return (this.accountUsers ?? []).some(user => user?.softphoneAgent === true)
    },

    // Staff only, and only where it applies: an impersonating super-admin looking at an account
    // that does softphone work. A regular user never sees it, and neither does an admin on an
    // account with no agents - there would be nothing for the note to be about.
    showSoftphoneAdminNotice() {
      return this.isImpersonating
        && this.canViewSoftphoneCampaigns
        && this.accountHasSoftphoneAgents
    },

    // The list is partitioned by user type rather than filtered: a softphone user sees only
    // softphone campaigns, everyone else sees only the standard ones. Campaigns stay on the
    // account either way - this only controls what the signed-in user is shown.
    //
    // A super-admin impersonating the account is the exception and reads it whole. The partition
    // exists because a softphone user has no page for a standard campaign and vice versa; staff
    // can open either edit page, and partitioning them would instead hide half the account from
    // the one person looking at it on the account's behalf.
    visibleCampaigns() {
      if (this.isImpersonating && this.canViewSoftphoneCampaigns) {
        return this.buyerCampaigns
      }

      return this.buyerCampaigns.filter(
        campaign => isSoftphoneCampaign(campaign) === this.isSoftphoneUser
      )
    },

    // Two filters, each for its own reason. First the market columns, for whoever is shown no
    // market data - see MARKET_COLUMNS. Then the same partition as the rows: a softphone agent
    // reads a shorter table - see COLUMNS_HIDDEN_FROM_AGENTS.
    //
    // Staff keep every column, including staff who are softphone agents in their own right. The
    // reduced table is only coherent alongside the agent's reduced ROW set: it drops Name, and an
    // admin is shown the account's standard campaigns too, which are then unidentifiable.
    visibleFields() {
      const fields = this.showMarketContext
        ? this.fields
        : this.fields.filter(field => !MARKET_COLUMNS.includes(field.key))

      if (!this.isSoftphoneUser || this.isImpersonating) {
        return fields
      }

      return fields.filter(field => !COLUMNS_HIDDEN_FROM_AGENTS.includes(field.key))
    },

    // Merges the market position onto each campaign row so the b-table can sort
    // on missedCalls natively. The endpoint returns a row for every campaign
    // whatever its routing type, so a null here means only that the campaign's market
    // cell had no auctions in the window, or that the fetch has not resolved yet -
    // never that the campaign is the wrong routing type. Those rows render as a dash.
    campaignsWithMarketData() {
      return this.visibleCampaigns.map(campaign => {
        const marketPosition = this.getMarketPositionForCampaign(campaign.id)

        return {
          ...campaign,
          marketPosition,
          missedCalls: marketPosition ? marketPosition.missedCalls : null
        }
      })
    },

    // Feeds the missed-calls banner. Routing type is deliberately not part of the filter:
    // a dynamic campaign whose median observed bid lands below market is genuinely
    // below market and its missed calls are real, so they count toward the banner's
    // total. A dynamic campaign that placed no bids carries tier: null and falls out
    // here on its own, with no extra check.
    belowMarketCampaigns() {
      return this.campaignsWithMarketData.filter(campaign => {
        return campaign.marketPosition
          && !this.isCampaignPaused(campaign)
          && (campaign.marketPosition.tier === bidTiers.BelowMarket || campaign.marketPosition.tier === bidTiers.PricedOut)
      })
    },

    // The two market columns explain themselves on hover, same pattern as missedCallsTooltip
    // below. Between them they name the two DIFFERENT populations these columns are scored
    // against - winning prices for the badge, other buyers' typical bids for the rank band -
    // which is the whole reason the pair exists; see getRankBandTooltip in the store. The
    // Opportunity one covers only the rank band, not the raise chip beside it: the chip reads
    // as its own sentence already
    marketPositionTooltip() {
      return getMarketPositionTooltip(this.marketWindow)
    },

    opportunityTooltip() {
      return getOpportunityTooltip(this.marketWindow)
    },

    // Spells out what the column counts, since "Missed" on its own reads like every call
    // the campaign did not get. Since 2026-09-04 the count is additionally gated on a call
    // having actually arrived - if the seller never sent one there was no call to miss at any
    // price - so the word "Calls" here is finally literal rather than a stand-in for "auctions
    // it lost". The copy needed no change for that; the number simply got smaller and honest.
    // The window phrase is dropped until the fetch resolves, for the same reason the column
    // label drops its suffix
    missedCallsTooltip() {
      const windowPhrase = this.marketWindow.long ? ` in the ${this.marketWindow.long}` : ''

      return `Calls this campaign bid on and lost to a higher bidder${windowPhrase}.`
        + ' Auctions it never entered are not counted.'
    },

    // The cap applies to the account's market cells and to its bidding campaigns, both of which
    // only outnumber it when the campaigns do - hence "campaigns"
    truncatedMarketPositionsText() {
      const cap = this.campaignMarketPositionsLimit
        ? ` (${this.campaignMarketPositionsLimit.toLocaleString('en-US')})`
        : ''

      return `This account has more campaigns than market data covers at once${cap}.`
        + ' Some campaigns are missing market data, and the missed-calls total leaves them out.'
    },

    // The row as the store has it NOW, not as it was when its raise chip was clicked: the modal
    // quotes this bid, and applySuggestedBid checks the raise against it
    selectedBidRaiseCampaign() {
      if (!this.selectedBidRaise) {
        return null
      }

      return this.buyerCampaigns.find(campaign => campaign.id === this.selectedBidRaise.campaign.id)
        || this.selectedBidRaise.campaign
    },

    totalMissedCalls() {
      return this.belowMarketCampaigns.reduce((total, campaign) => total + (campaign.missedCalls || 0), 0)
    },

    campaignsBelowMarketCount() {
      return this.belowMarketCampaigns.length
    }
  },

  watch: {
    // Covers every way the agent stops being live - the Go Offline button, the owning tab closing,
    // or another tab taking over - because all of them land here by clearing the live id.
    liveCampaignId(newCampaignId, oldCampaignId) {
      this.hideOpenTooltips()

      if (oldCampaignId && !newCampaignId) {
        this.startGoLiveCooldown()
      }
    },

    // The click disables the button, and a disabled button never receives the mouseleave that
    // would close its tooltip - so the text sat there until the page was clicked elsewhere.
    isTogglingSoftphone() {
      this.hideOpenTooltips()
    },

    // The one place the market positions are fetched on load: when the list finishes loading
    // inside the rollout, or later, when Datadog's answer turns the flag on. Turning false is the
    // kill switch reaching an open tab (or a logout), and the market sort and the raise-bid modal
    // go with the columns.
    showMarketContext(show) {
      if (show) {
        this.loadCampaignMarketPositions()

        return
      }

      // BootstrapVue keeps sorting by a key whose column has gone, which would leave the rows in
      // missed-calls order with nothing on screen to say so.
      if (MARKET_COLUMNS.includes(this.sortBy)) {
        this.sortBy = ''
        this.sortDesc = false
      }

      this.selectedBidRaise = null
      this.$bvModal.hide('confirm-raise-bid-modal')
    }
  },

  async created () {
    try {
      this.loading = true
      await Promise.all([
        this.fetchBuyerCampaigns(),
        this.getSoftphoneGoesLiveSetting()
      ])

      // `getBoolSetting` never rejects, so the setting, and with it the agent rule, is in by now.
      // This is what sets off the `showMarketContext` watcher.
      this.campaignsLoaded = true
    } catch (error) {
      if (error.response.status === 422) {
        this.showLoadingError = true
        this.loadingErrorText = error.response.data.title
      } else {
        showErrorMessage(error.response?.data?.title)
      }
    } finally {
      this.loading = false
    }

    // The market positions are not fetched here but by the `showMarketContext` watcher, so the
    // market query, which runs on ClickHouse and can be slow, does not hold up the staff note.
    await this.loadSoftphoneAgentPresence()
  },

  beforeDestroy() {
    this.clearGoLiveCooldown()
    clearTimeout(this.marketPositionsRefreshTimeout)
  },

  methods: {
    ...mapActions('campaign', [
      'fetchBuyerCampaigns',
      'updateCampaignBid'
    ]),

    ...mapActions('globalSettings', ['getSoftphoneGoesLiveSetting']),

    ...mapActions('account', ['getAccountUsers']),

    ...mapActions('billing', ['getBuyerAccountBalance']),

    ...mapActions('campaign', ['fetchBuyerCampaignBudgetState']),

    /**
     * The campaign's own daily budget, which the row in front of the agent cannot answer for: the
     * list endpoint returns the campaign ENTITY and the flag only exists on the DTO the
     * single-campaign endpoint builds. Without this the gate below was reading `undefined` and a
     * campaign that had spent its budget went on offering Go Live.
     *
     * Best effort, like the balance: null means the campaign's budget could not be read, which is
     * not evidence that it is spent.
     */
    async refreshCampaignBudgetBeforeGoingLive(campaign) {
      try {
        return await this.fetchBuyerCampaignBudgetState(campaign.id)
      } catch (error) {
        console.error('Could not re-check the campaign daily budget before going live', error)

        return null
      }
    },

    /**
     * Re-reads the balance so the go-live check below is about the account as it stands NOW.
     *
     * The button's disabled state is computed from a balance the header refreshes every 45 seconds,
     * and an account can run dry inside that window - which is precisely the click that would start
     * a silent shift. Going live is a deliberate, occasional act, so one request to be sure of it is
     * cheap.
     *
     * RETURNS the balance it fetched, and the caller gates on that rather than on the store, which
     * is not the same thing. Several places refresh the balance - this, the header's 45 second
     * poll, Add Funds, the billing pages - and two of them can be in flight at once, so
     * `SET_ACCOUNT_BALANCE` is last-writer-wins by arrival, not by recency. An older healthy
     * response landing after this one would put the account back over the line in the store, and
     * the gate would wave through the click this request was sent to refuse. The response in hand
     * cannot be overtaken.
     *
     * Best effort, and deliberately so: null means the balance could not be fetched, which is not
     * evidence that the account is broke. Refusing the shift over a failed request would be a
     * worse failure than the one this prevents, so the figure already in the store decides instead.
     */
    async refreshAccountBalanceBeforeGoingLive() {
      try {
        return await this.getBuyerAccountBalance()
      } catch (error) {
        console.error('Could not re-check the account balance before going live', error)

        return null
      }
    },

    /**
     * Loads the market positions behind the market columns and banner, for everyone who is shown
     * them (`showMarketContext`). Nothing is fetched for a softphone agent (see
     * `marketContext/canViewMarketContext`), or outside the rx-market-context rollout.
     *
     * Market context is a helper, never a blocker - the campaigns table must render even when this
     * fetch fails, so it stays out of `created`'s loading try/catch.
     */
    async loadCampaignMarketPositions() {
      if (!this.showMarketContext) {
        return
      }

      try {
        await this.fetchCampaignMarketPositions()
      } catch (error) {
        showErrorMessage(error.response?.data?.title || 'Failed to load market positions')
      }
    },

    /**
     * Loads the account's user list, but only for the one reader who needs it: an impersonating
     * admin, to decide whether the softphone note applies. Nobody else pays for the call.
     *
     * Kept out of `created`'s loading try/catch and swallowed on failure on purpose. This only decides
     * whether an informational alert renders, and the endpoint additionally requires the account
     * to have completed onboarding - a 403 there must not take the campaigns list down with it.
     */
    async loadSoftphoneAgentPresence() {
      if (!this.isImpersonating || !this.canViewSoftphoneCampaigns) {
        return
      }

      try {
        await this.getAccountUsers()
      } catch (error) {
        console.error('Could not read the account users to check for softphone agents', error)
      }
    },

    ...mapActions('campaign', {
      setResumeBuyerCampaignStatus: 'resumeBuyerCampaign',
      setPauseBuyerCampaignStatus: 'pauseBuyerCampaign',
      goLiveSoftphoneCampaign: 'goLiveSoftphoneCampaign'
    }),

    ...mapActions('marketContext', [
      'fetchCampaignMarketPositions'
    ]),

    ...mapActions('softphone', [
      'requestGoOnline',
      'requestGoOffline'
    ]),

    getBadgeVariantForCampaignStatus,

    getStatusText,

    isSoftphoneCampaign,

    formatCurrency,

    isCampaignPaused(campaign) {
      return campaign.status === PAUSED_STATUS || campaign.isPaused
    },

    // A softphone campaign is paused and resumed by going offline and live - the agent has to be
    // connected to take calls, so pausing or resuming it on its own would desync the widget.
    canPauseCampaign(campaign) {
      return !isSoftphoneCampaign(campaign)
    },

    showConfirmPauseBuyerCampaign(campaign) {
      this.selectedBuyerCampaign = campaign

      this.$bvModal.show('confirm-pause-modal')
    },

    async pauseBuyerCampaign() {
      try {
        this.isLoading = true
        await this.setPauseBuyerCampaignStatus({ campaignId: this.selectedBuyerCampaign.id })

        showSuccessMessage('Successfully paused buyer campaign')

        this.selectedBuyerCampaign = null

        this.$bvModal.hide('confirm-pause-modal')
      } catch (error) {
        showErrorMessage(error.response?.data?.title)
      } finally {
        this.isLoading = false
      }
    },

    showConfirmResumeBuyerCampaign(campaign) {
      this.selectedBuyerCampaign = campaign

      this.$bvModal.show('confirm-resume-modal')
    },

    async resumeBuyerCampaign() {
      try {
        this.isLoading = true
        await this.setResumeBuyerCampaignStatus({ campaignId: this.selectedBuyerCampaign.id, status: this.selectedBuyerCampaign.status })

        showSuccessMessage('Successfully resumed buyer campaign')

        this.selectedBuyerCampaign = null

        this.$bvModal.hide('confirm-resume-modal')
      } catch (error) {
        showErrorMessage(error.response?.data?.title)
      } finally {
        this.isLoading = false
      }
    },

    // Closes any tooltip currently open anywhere on the page. Used whenever a button this list
    // owns is about to be disabled, relabelled or removed under the pointer.
    hideOpenTooltips() {
      this.$root.$emit('bv::hide::tooltip')
    },

    isLiveSoftphoneCampaign(campaign) {
      return this.liveCampaignId === campaign.id
    },

    /**
     * The Status column, in the agent's own vocabulary on their own campaigns.
     *
     * A softphone campaign is not something the agent starts and stops - it is the campaign their
     * widget is on, or one it is not - so the row says what the widget says rather than what the
     * server calls the campaign underneath. The campaign the widget holds mirrors it exactly, down
     * to Connecting and Wrap-up; every other softphone campaign reads Offline, because that is what
     * the agent's softphone is on it.
     *
     * Review and rejection are the exception, and stay as they are: those are about whether the
     * campaign may EVER take calls, which no softphone status can say, and the Go Live button is
     * dead on them for exactly that reason.
     */
    statusText(campaign) {
      if (!this.isAgentStatusRow(campaign)) {
        return getStatusText(campaign)
      }

      return this.isLiveSoftphoneCampaign(campaign)
        ? this.softphoneStatusLabel
        : getSoftphoneStatusLabel(SOFTPHONE_STATUS.OFFLINE)
    },

    statusVariant(campaign) {
      if (!this.isAgentStatusRow(campaign)) {
        return getBadgeVariantForCampaignStatus(campaign)
      }

      return getSoftphoneStatusBadgeVariant(
        this.isLiveSoftphoneCampaign(campaign) ? this.softphoneStatus : SOFTPHONE_STATUS.OFFLINE
      )
    },

    /**
     * Which rows get the widget's vocabulary: the ones whose Go Live button this agent's own
     * softphone is behind. Deliberately the same test the button itself uses - a row whose status
     * the softphone speaks for, but whose button is missing or dead, is the mismatch this whole
     * change exists to remove.
     *
     * The campaign the widget is live for is in whatever the campaign looks like now, exactly as
     * `showSoftphoneButton` keeps Go Offline on it: an administrator strict-pausing a campaign
     * mid-call must not flip the row to "Paused" while the widget reads "On call".
     *
     * In review, rejected, expired, deleted and strict-paused rows keep the server's own status.
     * Those say whether the campaign may EVER take calls, which no softphone status can, and an
     * agent told their campaign is merely "Offline" would keep pressing a button that cannot work.
     */
    isAgentStatusRow(campaign) {
      if (!isSoftphoneCampaign(campaign) || !this.isOwnSoftphoneCampaign(campaign)) {
        return false
      }

      if (this.isLiveSoftphoneCampaign(campaign)) {
        return true
      }

      return canSoftphoneCampaignGoLive(campaign) && !this.isStrictPausedCampaign(campaign)
    },

    /**
     * The list covers the WHOLE ACCOUNT, colleagues' softphone campaigns included - see
     * `getTakenSoftphoneCampaignCombos`, which has to filter by the same field for the same reason.
     * Another agent's campaign says nothing about this agent's softphone: one of theirs sitting
     * Active with them on a call must not read "Offline" here just because this widget is not on it.
     */
    isOwnSoftphoneCampaign(campaign) {
      return Boolean(this.userId) && campaign?.createdBy === this.userId
    },

    /**
     * Going live is offered only where it can work. Approval is what creates the target the calls
     * arrive through, and a strict pause can only be lifted by an administrator, so on those rows
     * the button was permanently dead - the status badge already says why, and a disabled button
     * next to it only invites clicking.
     *
     * The row the agent is live for keeps its button whatever the campaign looks like now: that
     * button is Go Offline, and it is the only way back off a campaign an administrator has since
     * strict-paused.
     */
    showSoftphoneButton(campaign) {
      // Never for staff impersonating the account, including staff who are softphone agents
      // elsewhere: the campaign rings the account's agent, not them. The backend refuses both
      // halves anyway - go-live requires the campaign's CreatedBy to be the caller, and the WebRTC
      // token requires the account be one of the caller's own memberships - so the button could
      // only ever have errored.
      if (this.isImpersonating || !this.isSoftphoneAvailable || !isSoftphoneCampaign(campaign)) {
        return false
      }

      if (this.isLiveSoftphoneCampaign(campaign)) {
        return true
      }

      return canSoftphoneCampaignGoLive(campaign) && !this.isStrictPausedCampaign(campaign)
    },

    // An administrator paused it and only an administrator can resume it. Going live resumes the
    // campaign first, and the resume endpoint rejects a strict pause, so offering the button would
    // only open a window and then fail.
    isStrictPausedCampaign(campaign) {
      return campaign?.isStrictPause === true
    },

    /**
     * Why a shift started right now could not ring, or null. See
     * `getSoftphoneGoLiveBlockedReason` - an empty balance or a spent daily budget, both of which
     * make Ringba cap the buyer and route nothing.
     *
     * Deliberately NOT asked of the row the agent is already live for. That button is Go Offline,
     * and it is the only way off the campaign: an account that ran dry mid-shift must not take the
     * way out with it.
     *
     * `accountBalance` defaults to the store's copy, which is what the disabled state of every
     * button in the list is computed from. The click passes the response it just fetched instead -
     * see `refreshAccountBalanceBeforeGoingLive` for why the two are not interchangeable.
     */
    softphoneGoLiveBlockedReason(campaign, accountBalance = this.accountBalance) {
      if (this.isLiveSoftphoneCampaign(campaign)) {
        return null
      }

      return getSoftphoneGoLiveBlockedReason(campaign, accountBalance)
    },

    // Once the agent is live for one campaign every other Go Live is dead: they have one pair of
    // ears, and a second campaign routing to the same widget would be a call nobody answers.
    isSoftphoneButtonDisabled(campaign) {
      if (this.isTogglingSoftphone || this.isGoLiveCoolingDown) {
        return true
      }

      if (this.isLiveSoftphoneCampaign(campaign)) {
        return false
      }

      return !!this.liveCampaignId
        || this.isStrictPausedCampaign(campaign)
        || !canSoftphoneCampaignGoLive(campaign)
        /**
         * No funds, or the day's budget already spent. The campaign is perfectly able to take
         * calls and none would be bought, so going live would register the agent into silence.
         *
         * ACCOUNT-level caps only, in practice. Those flags are already in the store from the
         * header's poll, so they cost nothing to read here - but a CAMPAIGN's own daily budget
         * lives on the single-campaign endpoint, and the list returns the entity, which has no such
         * field. Disabling the button for it would mean one request per row on every visit to this
         * page, for a state most rows are not in.
         *
         * So a campaign over its own daily budget keeps a live-looking button and is refused on the
         * CLICK instead - see `goLiveForCampaign`, which fetches the campaign's budget before it
         * commits the agent to anything. Deliberate: the agent never goes live into silence, the
         * button merely looks available until they try. Closing it properly wants the flag on the
         * list response, which is a server-side change.
         */
        || Boolean(this.softphoneGoLiveBlockedReason(campaign))
    },

    softphoneButtonTitle(campaign) {
      if (this.isLiveSoftphoneCampaign(campaign)) {
        return 'Stop taking calls and pause this campaign'
      }

      if (this.isStrictPausedCampaign(campaign)) {
        return campaign.strictPauseReason
          ? `Paused by an administrator: ${campaign.strictPauseReason}`
          : 'Paused by an administrator and can only be resumed by one'
      }

      // Approval is what creates the target the calls arrive through, so an unapproved campaign
      // cannot ring however live the agent is. Said here rather than left to the go-live refusal:
      // the status badge alone does not explain why the button does nothing.
      if (!canSoftphoneCampaignGoLive(campaign)) {
        return campaign.status === 'InReview'
          ? 'Waiting for approval - you can go live once this campaign is approved'
          : 'This campaign cannot take calls'
      }

      if (this.isGoLiveCoolingDown) {
        return 'Finishing up your last session'
      }

      // The agent's own click is what disabled this button, and the go-live it started may still
      // be in flight - telling them they are live for ANOTHER campaign reads as a refusal of the
      // one they just clicked.
      if (this.isTogglingSoftphone) {
        return 'Already live'
      }

      /**
       * Ahead of "already live for another campaign", which is the generic below it: an account
       * that cannot buy calls is the bigger fact and the one the agent has to pass on to whoever
       * holds the card. Being live elsewhere is something they can see for themselves in their own
       * widget.
       */
      const blockedReason = this.softphoneGoLiveBlockedReason(campaign)

      if (blockedReason) {
        return blockedReason
      }

      if (this.isSoftphoneButtonDisabled(campaign)) {
        return 'You are already live for another campaign'
      }

      return 'Start taking calls for this campaign in this tab'
    },

    async toggleSoftphoneForCampaign(campaign) {
      if (this.isLiveSoftphoneCampaign(campaign)) {
        await this.goOfflineForCampaign()

        return
      }

      await this.goLiveForCampaign(campaign)
    },

    /**
     * Order matters and is not obvious.
     *
     * The server is told to go live FIRST - it pauses the agent's other softphone campaigns and
     * resumes this one - and only once that succeeds is the softphone told to connect, so a
     * go-live failure never leaves an agent taking calls for a campaign that is still paused.
     */
    async goLiveForCampaign(campaign) {
      // Belt and braces for the disabled button: never go live for a campaign the server is
      // guaranteed to refuse.
      if (this.isStrictPausedCampaign(campaign) || !canSoftphoneCampaignGoLive(campaign)) {
        showErrorMessage(this.softphoneButtonTitle(campaign))

        return
      }

      /**
       * Set BEFORE the first await, and it is the only thing holding the button.
       *
       * It is the first branch of `isSoftphoneButtonDisabled`, and nothing else disables the row
       * this early - `liveCampaignId` is still null until the server answers. Left until after the
       * gate, a second click during those round trips ran the whole thing again and sent a second
       * go-live for the same campaign. The gate below made that window twice as wide by putting
       * another request in front of the flag.
       */
      this.isTogglingSoftphone = true

      try {
        /**
         * Both caps, re-read at the point of no return, and both gating on the RESPONSE rather than
         * on anything already on screen.
         *
         * The balance because the store is last-writer-wins by arrival, so a slower overlapping
         * refresh can put an older reading back over the top. The campaign's budget because the row
         * NEVER carried it - the list endpoint cannot - so the only honest answer is a fetched one.
         *
         * In parallel: two independent reads of the same account, and going live is a click the
         * agent is waiting on.
         */
        const [freshAccountBalance, campaignBudgetState] = await Promise.all([
          this.refreshAccountBalanceBeforeGoingLive(),
          this.refreshCampaignBudgetBeforeGoingLive(campaign)
        ])

        const blockedReason = this.softphoneGoLiveBlockedReason(
          { ...campaign, ...campaignBudgetState },
          freshAccountBalance ?? this.accountBalance
        )

        if (blockedReason) {
          showErrorMessage(blockedReason)

          return
        }

        await this.goLiveSoftphoneCampaign({ campaignId: campaign.id, status: campaign.status })

        // The whole row: the action picks off what the softphone needs, including the budget's
        // minimum duration, which a tab that holds the softphone without holding this list has no
        // other way to learn.
        //
        // Not awaited, and its failure deliberately swallowed: going live reports itself through
        // the softphone's status, which is what `waitForSoftphoneToConnect` below reads - in this
        // tab or in whichever one owns the transport.
        Promise.resolve(this.requestGoOnline(campaign)).catch(() => {})

        // The server resuming the campaign is only half of going live - the agent is not taking
        // calls until the softphone has registered. Announcing success on the server call alone
        // told agents they were live while the softphone was still failing to connect.
        const connection = await this.waitForSoftphoneToConnect()

        if (connection.status === 'failed') {
          showErrorMessage(connection.error || 'The phone could not connect, so you are not taking calls')

          return
        }

        if (connection.status === 'timedOut') {
          showWarningMessage('The campaign was resumed, but you are not connected yet - check the status in the header')

          return
        }

        // No success notification: the softphone panel opens itself on going live, and a panel
        // reading Ready says the same thing in the place the agent is about to work from. See the
        // `softphoneAttention` watcher in SoftphoneHeaderWidget.
      } catch (error) {
        showErrorMessage(error.response?.data?.title || 'Could not go live for this campaign')
      } finally {
        this.isTogglingSoftphone = false
      }
    },

    /**
     * Resolves once the softphone has said what became of the go-live: `connected` when it is
     * registered and taking calls, `failed` when it gave up (the token call, the transport, or the
     * owning tab going away on the way), `timedOut` when it has said nothing either way.
     *
     * Reads the softphone store's status and nothing else, which works whether this tab owns the
     * transport or is only being told about it.
     */
    waitForSoftphoneToConnect() {
      return new Promise(resolve => {
        let unwatch = null
        let timeoutId = null

        // A failed go-live resets the widget to OFFLINE and only then sets ERROR with the reason,
        // so a bare OFFLINE waits a moment for that reason rather than reporting without it.
        let failureGraceTimerId = null

        let hasStartedConnecting = false

        const finish = result => {
          if (unwatch) {
            unwatch()
            unwatch = null
          }

          clearTimeout(timeoutId)
          clearTimeout(failureGraceTimerId)

          timeoutId = null
          failureGraceTimerId = null

          resolve(result)
        }

        const fail = () => finish({ status: 'failed', error: this.$store.state.softphone.lastError })

        const evaluate = status => {
          if (status === SOFTPHONE_STATUS.CONNECTING) {
            hasStartedConnecting = true

            return false
          }

          if (CONNECTED_STATUSES.includes(status)) {
            finish({ status: 'connected' })

            return true
          }

          // ERROR is the softphone's own report that it could not go live - it broadcasts the
          // reason with it. OTHER_TAB is another tab taking the softphone over, which leaves this
          // go-live just as dead.
          if (status === SOFTPHONE_STATUS.ERROR || status === SOFTPHONE_STATUS.OTHER_TAB) {
            fail()

            return true
          }

          // Back to offline after it began connecting: the softphone tore down, whether it went on
          // to say why or its tab simply went away. Offline BEFORE that is the softphone dropping a
          // previous campaign on its way to this one, which is part of a normal go-live.
          if (status === SOFTPHONE_STATUS.OFFLINE && hasStartedConnecting && !failureGraceTimerId) {
            failureGraceTimerId = setTimeout(fail, SOFTPHONE_FAILURE_REASON_GRACE_MS)
          }

          return false
        }

        // The widget can register before this tab gets to subscribe.
        if (evaluate(this.$store.state.softphone.status)) {
          return
        }

        unwatch = this.$store.watch(state => state.softphone.status, evaluate)

        timeoutId = setTimeout(() => finish({ status: 'timedOut' }), SOFTPHONE_CONNECT_TIMEOUT_MS)
      })
    },

    /**
     * The owning tab goes offline end to end - it disconnects and pauses the campaign, in that
     * order, then broadcasts the result so every other tab's rows come back to life. When that tab
     * is this one the action simply does it; when it is another, this asks.
     */
    async goOfflineForCampaign() {
      try {
        this.isTogglingSoftphone = true

        // No success notification either way: the softphone panel and the row's own button both
        // say what happened, and they say it where the agent is looking.
        await this.requestGoOffline()
      } catch (error) {
        showErrorMessage('Could not take you offline')
      } finally {
        this.isTogglingSoftphone = false
      }
    },

    startGoLiveCooldown() {
      this.clearGoLiveCooldown()

      this.isGoLiveCoolingDown = true

      this.goLiveCooldownTimer = setTimeout(() => {
        this.goLiveCooldownTimer = null
        this.isGoLiveCoolingDown = false
      }, GO_LIVE_COOLDOWN_MS)
    },

    clearGoLiveCooldown() {
      if (this.goLiveCooldownTimer) {
        clearTimeout(this.goLiveCooldownTimer)
        this.goLiveCooldownTimer = null
      }

      this.isGoLiveCoolingDown = false
    },

    // Softphone campaigns open in their own reduced page - the standard edit page's Number/SIP
    // destination form cannot represent a softphone destination.
    editRouteName(campaign) {
      return isSoftphoneCampaign(campaign) ? 'BuyerEditSoftphoneCampaign' : 'BuyerEditCampaign'
    },

    getMaxBidPerCall(campaign) {
      return campaign.routing.type === campaignRoutingTypes.Static ? this.formatCurrency(campaign.budget.maxBidPerCallInUSD) : 'Dynamic'
    },

    getCategoryMinBid(campaign) {
      return getStaticCampaignMinBid(campaign)
    },

    isStaticCampaign(campaign) {
      return campaign.routing.type === campaignRoutingTypes.Static
    },

    // The inline bid editor is part of the rx-market-context rollout, and the flag alone decides
    // it. A softphone agent therefore keeps the editor on their own campaigns, which are all
    // Static, while the flag is on, and the flag is read, and exposed, for them here. It is read
    // last, so a dynamic row never reads it.
    canEditBidInline(campaign) {
      return this.isStaticCampaign(campaign) && this.marketContextEnabled
    },

    // The bid the endpoint positioned this campaign on, null when a dynamic campaign
    // placed no bids in the window (in which case there is no marker to draw)
    getPositionedBid(campaign) {
      return campaign.marketPosition ? campaign.marketPosition.positionedBidInUSD : null
    },

    getMissedCallsClass(campaign) {
      if (!campaign.marketPosition) {
        return 'text-muted'
      }

      switch (campaign.marketPosition.tier) {
      case bidTiers.PricedOut:
        return 'text-danger'
      case bidTiers.BelowMarket:
        return 'text-warning'
      default:
        return 'text-muted'
      }
    },

    sortByMissedCalls() {
      this.sortBy = 'missedCalls'
      this.sortDesc = true
    },

    showConfirmRaiseBid(bidRaise) {
      this.selectedBidRaise = bidRaise

      this.$bvModal.show('confirm-raise-bid-modal')
    },

    // An inline save has put the new bid in the store, but the row's market position was scored
    // on the old one until it is refetched. Debounced because a run of arrow clicks saves once
    // per click, and every refetch is a ClickHouse query.
    onInlineBidSaved() {
      clearTimeout(this.marketPositionsRefreshTimeout)

      this.marketPositionsRefreshTimeout = setTimeout(() => {
        this.marketPositionsRefreshTimeout = null
        this.loadCampaignMarketPositions()
      }, 1000)
    },

    async applySuggestedBid() {
      const campaign = this.selectedBidRaiseCampaign

      // The chip is hidden while a row's position is stale, but this is the last stop before a
      // real write: a "raise" to or below the bid the campaign already has would lower it
      if (toCents(this.selectedBidRaise.suggestedBidInUSD) <= toCents(campaign.budget.maxBidPerCallInUSD)) {
        showWarningMessage('This campaign\'s bid is already at or above the suggested bid')
        this.selectedBidRaise = null
        this.$bvModal.hide('confirm-raise-bid-modal')

        return
      }

      // A raise that still leaves the bid under the category minimum keeps the campaign off
      const minBid = getStaticCampaignMinBid(campaign)
      if (toCents(this.selectedBidRaise.suggestedBidInUSD) < toCents(minBid)) {
        showErrorMessage(getMinBidMessage(minBid))
        this.selectedBidRaise = null
        this.$bvModal.hide('confirm-raise-bid-modal')

        return
      }

      try {
        this.isLoading = true
        await this.updateCampaignBid({
          campaignId: campaign.id,
          bidValue: this.selectedBidRaise.suggestedBidInUSD
        })

        showSuccessMessage('Successfully updated campaign bid')

        this.selectedBidRaise = null

        this.$bvModal.hide('confirm-raise-bid-modal')

        // Refresh both the campaigns (new bid) and the market positions (new tier). The positions
        // go through the same gate as on load, in case the flag turned off meanwhile.
        await Promise.all([this.fetchBuyerCampaigns(), this.loadCampaignMarketPositions()])
      } catch (error) {
        showErrorMessage(error.response?.data?.title || 'Failed to update campaign bid')
      } finally {
        this.isLoading = false
      }
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep .panel__header h6 {
  flex-grow: 1;
}

/*
 * The On call badge is the pale one - the widget leaves its own the colour of the panel behind it,
 * for a state the agent is already living through rather than one they have to be told about. That
 * only reads as a badge against a border: `badge-light` on a white table cell is a floating word.
 * The widget draws the same border on the same badge, for the same reason.
 */
.campaign-status.badge-light {
  box-shadow: inset 0 0 0 1px #ced4da;
}

/*
 * The bid editor is 33px tall in rows of 21px text lines, and the cells are top-aligned. Hung from
 * the top of the line and pulled 6px into the cell padding either side, it centres on the text in
 * the rest of the row, and a row that has it is no taller than one that reads "Dynamic".
 */
.inline-bid-input {
  vertical-align: top;
  margin: -6px 0;
}
</style>